import React, { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Pencil, Check, X, Loader2 } from "lucide-react";
import { useSettings } from "../../lib/settings";

export default function AvailabilityCalendar({ showDates = [], availableIds = [], editable = false, onSave }) {
  const { settings, formatFull } = useSettings();

  const showDatesByYmd = useMemo(() => {
    const m = {};
    showDates.forEach((sd) => { m[sd.date] = sd; });
    return m;
  }, [showDates]);

  const availSet = useMemo(() => new Set(availableIds || []), [availableIds]);

  // Edit mode (admin can adjust an actor's availability directly on the calendar).
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(() => new Set(availableIds || []));
  const [saving, setSaving] = useState(false);
  const activeSet = editing ? draft : availSet;
  const startEdit = () => { setDraft(new Set(availableIds || [])); setEditing(true); };
  const cancelEdit = () => { setEditing(false); };
  const toggleDate = (id) => setDraft((d) => { const n = new Set(d); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const doSave = async () => {
    if (!onSave) return;
    setSaving(true);
    try { await onSave(Array.from(draft)); setEditing(false); }
    finally { setSaving(false); }
  };

  const [view, setView] = useState(null);
  const [userNavigated, setUserNavigated] = useState(false);
  const initialisedRef = React.useRef(false);

  // Initialize once to first show date's month (or current month).
  useEffect(() => {
    if (initialisedRef.current) return;
    if (showDates.length > 0) {
      const sorted = [...showDates].sort((a, b) => a.date.localeCompare(b.date));
      const d = new Date(sorted[0].date);
      setView({ y: d.getFullYear(), m: d.getMonth() });
      initialisedRef.current = true;
    } else if (!view) {
      const now = new Date();
      setView({ y: now.getFullYear(), m: now.getMonth() });
      // don't mark initialised — we may still want to re-initialise once show dates arrive
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDates]);

  if (!view) return null;

  const prevMonth = () => { setUserNavigated(true); setView((v) => v.m === 0 ? { y: v.y - 1, m: 11 } : { y: v.y, m: v.m - 1 }); };
  const nextMonth = () => { setUserNavigated(true); setView((v) => v.m === 11 ? { y: v.y + 1, m: 0 } : { y: v.y, m: v.m + 1 }); };

  // Build calendar cells: Mon-Sun grid for the month
  const firstOfMonth = new Date(view.y, view.m, 1);
  const daysInMonth = new Date(view.y, view.m + 1, 0).getDate();
  // JS getDay: 0=Sun..6=Sat. Convert to Mon=0..Sun=6
  const startWeekday = (firstOfMonth.getDay() + 6) % 7;
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const ymd = (day) => `${view.y}-${String(view.m + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

  const totalShows = showDates.length;
  const availableCount = showDates.filter((sd) => activeSet.has(sd.id)).length;

  const monthLabel = firstOfMonth.toLocaleDateString(settings.date_format === "us" ? "en-US" : "en-GB", { month: "long", year: "numeric" });

  const today = new Date();
  const isToday = (day) => view.y === today.getFullYear() && view.m === today.getMonth() && day === today.getDate();

  const weekHeaders = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  return (
    <div className="border border-[#1a1a1a] bg-[#0A0A0A] p-5" data-testid="availability-calendar">
      <div className="flex items-center justify-between mb-4">
        <h3 className="label-mini">Availability</h3>
        <div className="flex items-center gap-3">
          <span className="font-mono text-xs text-[#A19D94]">
            Available <span className="text-blood font-semibold">{availableCount}</span> / {totalShows}
          </span>
          {editable && !editing && (
            <button onClick={startEdit} data-testid="availability-edit-btn" title="Edit availability"
              className="p-1.5 border border-[#262626] hover:border-blood hover:text-blood text-[#A19D94]"><Pencil size={13} /></button>
          )}
          {editing && (
            <div className="flex items-center gap-1.5">
              <button onClick={doSave} disabled={saving} data-testid="availability-save-btn"
                className="flex items-center gap-1 px-2.5 py-1.5 text-[10px] uppercase tracking-wider bg-blood text-white hover:bg-[#FF3333] disabled:opacity-50">
                {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Save
              </button>
              <button onClick={cancelEdit} disabled={saving} data-testid="availability-cancel-btn"
                className="flex items-center gap-1 px-2.5 py-1.5 text-[10px] uppercase tracking-wider border border-[#262626] text-[#A19D94] hover:border-[#3a3835]"><X size={12} /> Cancel</button>
            </div>
          )}
        </div>
      </div>

      {editing && <p className="text-[11px] text-[#F59E0B] mb-3" data-testid="availability-edit-hint">Editing — tap an open date to toggle availability, then Save.</p>}

      <div className="flex items-center justify-between mb-3">
        <button onClick={prevMonth} data-testid="cal-prev" className="p-1.5 border border-[#262626] hover:border-blood hover:text-blood text-[#A19D94]">
          <ChevronLeft size={14} />
        </button>
        <div className="font-display text-lg uppercase tracking-tight" data-testid="cal-month-label">{monthLabel}</div>
        <button onClick={nextMonth} data-testid="cal-next" className="p-1.5 border border-[#262626] hover:border-blood hover:text-blood text-[#A19D94]">
          <ChevronRight size={14} />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-1">
        {weekHeaders.map((d) => <div key={d} className="text-[9px] text-center text-[#6B6862] font-mono uppercase tracking-wider py-1">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (d === null) return <div key={i} className="aspect-square" />;
          const date = ymd(d);
          const sd = showDatesByYmd[date];
          let state = "non_show";
          if (sd) state = activeSet.has(sd.id) ? "available" : "unavailable";
          const styles = {
            non_show: "border-transparent text-[#3a3835]",
            unavailable: "border border-[#7F1D1D]/40 bg-[#7F1D1D]/10 text-[#7F1D1D]",
            available: "border border-[#10B981] bg-[#10B981]/15 text-[#10B981]",
          }[state];
          const clickable = editing && !!sd;
          const Tag = clickable ? "button" : "div";
          return (
            <Tag
              key={i}
              type={clickable ? "button" : undefined}
              onClick={clickable ? () => toggleDate(sd.id) : undefined}
              data-testid={clickable ? `availability-cell-${sd.id}` : undefined}
              title={sd ? `${formatFull(sd.date)} · ${sd.name} · ${state === "available" ? "Available" : "Not available"}${clickable ? " (tap to toggle)" : ""}` : ""}
              className={`aspect-square w-full flex flex-col items-center justify-center text-sm font-mono ${styles} ${isToday(d) ? "ring-1 ring-[#A19D94]/40" : ""} ${clickable ? "cursor-pointer hover:ring-1 hover:ring-blood transition-all" : ""}`}
            >
              <span className="font-display text-base font-bold leading-none">{d}</span>
              {sd && <span className="text-[8px] uppercase tracking-wider mt-0.5 opacity-70">●</span>}
            </Tag>
          );
        })}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-4 text-[10px] text-[#A19D94] font-mono uppercase tracking-wider">
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 border border-[#10B981] bg-[#10B981]/15" /> Available</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 border border-[#7F1D1D]/40 bg-[#7F1D1D]/10" /> Unavailable</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 border border-[#262626]" /> No open date</span>
      </div>
    </div>
  );
}
